import React, { useState } from 'react';
import { Patient, TimelineTag } from '../types/patient';
import {
  Stethoscope,
  TestTube,
  Pill,
  Scissors,
  Activity,
  Heart,
  Droplet,
  Brain,
  Bone,
  Eye,
  Ear,
  Wind
} from 'lucide-react';

interface MedicalTimelineProps {
  patient: Patient;
}

// Icon mapping for timeline tags
const getIconForTag = (iconName: string) => {
  const icons: Record<string, React.ElementType> = {
    'Stethoscope': Stethoscope,
    'TestTube': TestTube,
    'Pill': Pill,
    'Scissors': Scissors,
    'Activity': Activity,
    'Heart': Heart,
    'Droplet': Droplet,
    'Brain': Brain,
    'Bone': Bone,
    'Eye': Eye,
    'Ear': Ear,
    'Lungs': Wind
  };
  return icons[iconName] || Activity;
};

// Color mapping for different tag types
const getTagColorClasses = (color: string) => {
  const colorMap: Record<string, string> = {
    'blue': 'bg-blue-100 text-blue-800 border-blue-200',
    'red': 'bg-red-100 text-red-800 border-red-200',
    'green': 'bg-green-100 text-green-800 border-green-200',
    'purple': 'bg-purple-100 text-purple-800 border-purple-200',
    'orange': 'bg-orange-100 text-orange-800 border-orange-200',
    'pink': 'bg-pink-100 text-pink-800 border-pink-200',
    'yellow': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'indigo': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    'teal': 'bg-teal-100 text-teal-800 border-teal-200',
    'cyan': 'bg-cyan-100 text-cyan-800 border-cyan-200'
  };
  return colorMap[color] || 'bg-gray-100 text-gray-800 border-gray-200';
};

export default function MedicalTimeline({ patient }: MedicalTimelineProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Get all unique tags from the timeline
  const allTags = new Set<string>();
  Object.values(patient.medicalTimeline || {}).forEach(event => {
    event.tags.forEach(tag => allTags.add(tag.label));
  });
  const uniqueTags = Array.from(allTags);

  // Sort timeline entries by date (most recent first)
  const sortedTimeline = Object.entries(patient.medicalTimeline || {})
    .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());

  // Filter timeline by selected tags
  const filteredTimeline = selectedTags.length > 0
    ? sortedTimeline.filter(([_, event]) =>
        event.tags.some(tag => selectedTags.includes(tag.label))
      )
    : sortedTimeline;

  const toggleTag = (tagLabel: string) => {
    setSelectedTags(prev =>
      prev.includes(tagLabel)
        ? prev.filter(t => t !== tagLabel)
        : [...prev, tagLabel]
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h2 className="text-xl font-bold text-brand-dark-600 mb-1">Medical History Timeline</h2>
        <p className="text-sm text-brand-taupe-600">
          Chronological view of {patient.firstName}'s medical events, procedures, and visits
        </p>
      </div>

      {/* Filter Tags */}
      {uniqueTags.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-brand-dark-600 mb-3">Filter by Category</h3>
          <div className="flex flex-wrap gap-2">
            {uniqueTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  selectedTags.includes(tag)
                    ? 'bg-brand-dark-500 text-white border-brand-dark-500'
                    : 'bg-white text-brand-taupe-700 border-brand-taupe-300 hover:border-brand-dark-400'
                }`}
              >
                {tag}
              </button>
            ))}
            {selectedTags.length > 0 && (
              <button
                onClick={() => setSelectedTags([])}
                className="px-3 py-1.5 rounded-full text-sm font-medium border bg-white text-brand-taupe-700 border-brand-taupe-300 hover:border-red-400 hover:text-red-600"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        {filteredTimeline.length === 0 ? (
          <div className="text-center py-8 text-brand-taupe-600">
            {selectedTags.length > 0
              ? 'No events match the selected filters'
              : 'No medical history events recorded'}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTimeline.map(([date, event], index) => (
              <div key={date} className="relative">
                {/* Timeline connector line */}
                {index !== filteredTimeline.length - 1 && (
                  <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-brand-taupe-300" />
                )}

                <div className="flex gap-3">
                  {/* Timeline dot */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-dark-500 flex items-center justify-center relative z-10">
                    <Activity className="h-4 w-4 text-white" />
                  </div>

                  {/* Event content */}
                  <div className="flex-1 pb-3">
                    <div className="bg-brand-taupe-50 rounded-lg p-3 border border-brand-taupe-200">
                      {/* Date */}
                      <div className="text-sm font-semibold text-brand-dark-600 mb-1">
                        {formatDate(date)}
                      </div>

                      {/* Content */}
                      <p className="text-sm text-brand-taupe-800 mb-2 leading-snug">
                        {event.content}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {event.tags.map((tag, tagIndex) => {
                          const Icon = tag.icon ? getIconForTag(tag.icon) : null;
                          const colorClasses = tag.color ? getTagColorClasses(tag.color) : 'bg-gray-100 text-gray-800 border-gray-200';

                          return (
                            <div
                              key={`${date}-tag-${tagIndex}`}
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${colorClasses}`}
                            >
                              {Icon && <Icon className="h-3 w-3" />}
                              {tag.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
